import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Fab from "@mui/material/Fab";
import Collapse from "@mui/material/Collapse";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
// Material Dashboard 2 React example components
import SidenavCollapse from "components/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "components/Sidenav/SidenavRoot";
import sidenavLogoLabel from "components/Sidenav/styles/sidenav";
// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setFixedNavbar,
} from "context";
import { Button, Grid } from "@mui/material";
import Authorize from "../Authorize";
import { ModeAuthorizeEnum } from "../../utils/constants";
import { useAuthorization } from "../../hooks/useAuthorization";

function Sidenav({ color = "info", brand = "", brandName, routes, ...rest }) {
  const auththor = useAuthorization();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } =
    controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");
  const [open, setOpen] = useState(false);

  const [activeCollapse, setActiveCollapseName] = useState("dashboard");
  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      // setMiniSidenav(dispatch, activeCollapse.includes("dashboard") && true);
      setTransparentSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : transparentSidenav
      );
      setWhiteSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : whiteSidenav
      );
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  useEffect(() => {
    document.body.style.overflow = "auto";
  }, []);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleClick = (key) => (e) => {
    setActiveCollapseName(key);
    setMiniSidenav(dispatch, false);
    if (key == activeCollapse) {
      setOpen((prev) => !prev);
    } else {
      setOpen(true);
    }
  };
  const handleToClickItemMenu = () => {
    setMiniSidenav(dispatch, true);
  };
  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    (
      {
        type,
        name,
        icon,
        access,
        title,
        collapse,
        noCollapse,
        key,
        href,
        route,
        hidingChildren,
        hiding,
      },
      index
    ) => {
      let returnValue;
      if (type === "collapse") {
        if (hidingChildren) {
          returnValue = (
            <Authorize
              mode={ModeAuthorizeEnum.view}
              permission={key}
              key={key + index}
            >
              <NavLink key={key} to={route}>
                <SidenavCollapse
                  name={name}
                  icon={icon}
                  active={key === collapseName}
                />
              </NavLink>
            </Authorize>
          );
        } else if (collapse && access != 3) {
          if (hiding) return null;
          returnValue = (
            <Authorize
              mode={ModeAuthorizeEnum.view}
              routerGroup={collapse}
              key={key + index}
            >
              <NavLink key={key} to={route} onClick={handleClick(key)}>
                <SidenavCollapse
                  name={name}
                  icon={icon}
                  active={collapseName.includes(key)}
                  iconRight={
                    key == activeCollapse && open ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )
                  }
                />
              </NavLink>
              <Collapse
                in={key == activeCollapse && open}
                timeout="auto"
                unmountOnExit
              >
                {collapse.map((child, childindex) => {
                  if (child?.hiding) return null;
                  return (
                    <Authorize
                      mode={ModeAuthorizeEnum.view}
                      permission={child?.key}
                      key={child.key + childindex}
                    >
                      <NavLink to={child.route} onClick={handleToClickItemMenu}>
                        <SidenavCollapse
                          name={child.name}
                          icon={child.icon}
                          active={child.key === collapseName}
                          style={{ marginLeft: "30px" }}
                        />
                      </NavLink>
                    </Authorize>
                  );
                })}
              </Collapse>
            </Authorize>
          );
        } else {
          if (hiding) return null;
          returnValue =
            access != 3 ? (
              <Authorize
                mode={ModeAuthorizeEnum.view}
                permission={key}
                key={key + index}
              >
                <NavLink key={key} to={route}>
                  <SidenavCollapse
                    name={name}
                    icon={icon}
                    active={key === collapseName}
                  />
                </NavLink>
              </Authorize>
            ) : null;
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = <Divider key={key} />;
      }
      return returnValue;
    }
  );
  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox mt={3} mb={1} px={4} textAlign="center" p={0}>
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          // onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        ></MDBox>
        <MDBox
          component={NavLink}
          to="/dashboard"
          display="flex"
          alignItems="center"
          sx={{ marginLeft: miniSidenav ? "2rem" : 0 }}
        >
          {brand && (
            <MDBox component="img" src={brand} alt="Brand" width="2rem" />
          )}
          <MDBox
            style={{
              textAlign: "left",
              whiteSpace: "normal",
              display: miniSidenav ? "none" : "block",
            }}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography
              component="h6"
              variant="button"
              fontWeight="medium"
              color={textColor}
              sx={{ letterSpacing: "0.02em" }}
            >
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <Grid item container direction="row" justifyContent="flex-end">
        <Fab
          size="small"
          sx={{
            color: "#fff",
            background: "linear-gradient(195deg, #3F51B5, #3f51b545)",
            position: "absolute",
            right: 0,
            lineHeight: "20px",
            borderRadius: "50%",
            top: "60px",
            cursor: "pointer",
          }}
          color="info"
          aria-label="add"
          onClick={handleMiniSidenav}
        >
          {miniSidenav ? (
            <KeyboardArrowRightOutlinedIcon />
          ) : (
            <KeyboardArrowLeftOutlinedIcon />
          )}
        </Fab>
      </Grid>
      <List>{renderRoutes}</List>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
    </SidenavRoot>
  );
}

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
